/**
 * CyberSweep date helpers. Gobodo / Salesforce payloads may use ISO strings, epoch **milliseconds**,
 * or epoch **seconds** (10-digit integers — e.g. due dates shown as 1776124800 in the UI if not normalized).
 */

export function parseCyberSweepDateToMs(val: unknown): number | null {
  if (val == null || val === '') return null;
  const s = String(val).trim();
  if (!s) return null;

  if (typeof val === 'number' && Number.isFinite(val) && val > 0) {
    if (val >= 1e9 && val < 1e12) return val * 1000;
    if (val >= 1e12) return val;
  }

  if (/^\d+(\.\d+)?$/.test(s)) {
    const n = Number(s);
    if (!Number.isFinite(n) || n <= 0) return null;
    if (n >= 1e9 && n < 1e12) return n * 1000;
    if (n >= 1e12) return n;
  }

  const parsed = Date.parse(s);
  if (Number.isFinite(parsed)) return parsed;
  return null;
}

/** Short US locale date for cards and tables (matches prior CyberSweep formatting). */
export function formatCyberSweepDisplayDate(val: unknown): string {
  const ms = parseCyberSweepDateToMs(val);
  if (ms == null) {
    const fallback = String(val ?? '').trim();
    return fallback.length > 10 ? fallback.slice(0, 10) : fallback;
  }
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Date + time for detail fields; uses the same epoch/ISO rules as {@link formatCyberSweepDisplayDate}. */
export function formatCyberSweepDisplayDateTime(val: unknown): string {
  const ms = parseCyberSweepDateToMs(val);
  if (ms == null) {
    const fallback = String(val ?? '').trim();
    return fallback.length > 10 ? fallback.slice(0, 10) : fallback;
  }
  return new Date(ms).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}
